import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshButton from '../components/RefreshButton';
import InfoIcon from '@mui/icons-material/Info';
import RestoreIcon from '@mui/icons-material/Restore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import {
  fileService,
  MAX_FILE_SIZE_BYTES,
  blockedUploadExtension,
  isSessionExpiredError,
  usesChunkedUpload,
} from '../services/file.service';
import { formatResumeWindow } from '../services/direct-upload.service';
import { calculateFileHashWithProgress } from '../utils/file-hash';
import type { BackupFile, RecycleBinFile, UploadFileStatus, FileSortField } from '../types/file.types';
import { useErrorStore } from '@/stores/errorStore';
import { useAuthStore } from '@/stores/authStore';
import { useUploadPolicy } from '@/hooks/useAuthConfig';

// QA #102: an upload whose login session ended mid-transfer (e.g. offline
// past the 30-minute idle window) says so, rather than 「上傳系統失敗」 with
// the backend's "unauthorized access to file".
const UPLOAD_SESSION_EXPIRED_MESSAGE = '登入已逾時，請重新登入後再上傳';

// files.backup_source labels, shared by the list column and the detail dialog (QA #42)
const BACKUP_SOURCE_LABELS: Record<string, string> = { manual: '手動上傳', watcher: 'Watcher', schedule: '排程' };
const BACKUP_SOURCE_COLORS: Record<string, 'default' | 'primary' | 'secondary'> = {
  manual: 'default',
  watcher: 'primary',
  schedule: 'secondary',
};

// QA #70: the upload dialog says how fast the connection is and, from that,
// above which size a File Watcher schedule is the better tool. "Better" =
// the browser would be busy for more than WATCHER_ADVICE_MINUTES.
const WATCHER_ADVICE_MINUTES = 30;

const formatSpeed = (bytesPerSec: number) =>
  bytesPerSec >= 1_000_000 ? `${(bytesPerSec / 1_000_000).toFixed(1)} MB/s` : `${Math.round(bytesPerSec / 1000)} KB/s`;

// Chromium exposes an effective-link estimate (Mbps); Firefox / Safari do not,
// in which case the advice falls back to the static 1 GB rule of thumb.
const browserLinkMbps = (): number | null => {
  const conn = (navigator as Navigator & { connection?: { downlink?: number } }).connection;
  return typeof conn?.downlink === 'number' && conn.downlink > 0 ? conn.downlink : null;
};

const uploadAdvice = (bytesPerSec: number, source: 'measured' | 'estimated' | 'unknown'): string => {
  if (bytesPerSec <= 0 || source === 'unknown') {
    return '單一檔案大於 1 GB 建議改用 File Watcher 排程備份：瀏覽器上傳速度較慢，關閉分頁就會中斷；File Watcher 會在背景分段上傳並自動續傳。';
  }
  const mbps = (bytesPerSec * 8) / 1_000_000;
  const minutesPerGB = 1_000_000_000 / bytesPerSec / 60;
  const perGB = minutesPerGB < 1 ? '不到 1 分鐘' : `${Math.round(minutesPerGB)} 分鐘`;
  const thresholdGB = Math.max(0.5, Math.round(((bytesPerSec * WATCHER_ADVICE_MINUTES * 60) / 1_000_000_000) * 10) / 10);
  const label = source === 'measured' ? '目前上傳速度' : '瀏覽器估計的連線速度';
  return `${label}約 ${mbps.toFixed(1)} Mbps（${formatSpeed(bytesPerSec)}），1 GB 約需 ${perGB}；超過約 ${thresholdGB} GB（${WATCHER_ADVICE_MINUTES} 分鐘以上）建議改用 File Watcher 排程備份：瀏覽器上傳關閉分頁就會中斷，File Watcher 會在背景分段上傳並自動續傳。`;
};

export default function Files() {
  const [files, setFiles] = useState<BackupFile[]>([]);
  const [recycleBin, setRecycleBin] = useState<RecycleBinFile[]>([]);
  const [userFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const showError = useErrorStore((s) => s.showError);
  const showSuccess = useErrorStore((s) => s.showSuccess);

  // 分頁狀態
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalFiles, setTotalFiles] = useState(0);

  // 回收站分頁狀態 (server-side; backend ListOptions default limit=20)
  const [recyclePage, setRecyclePage] = useState(0);
  const [recycleRowsPerPage, setRecycleRowsPerPage] = useState(20);
  const [recycleTotal, setRecycleTotal] = useState(0);
  const [recycleRetentionDays, setRecycleRetentionDays] = useState<number | null>(null);

  // 搜尋與過濾
  const [searchKeyword, setSearchKeyword] = useState('');
  // QA #11: status filter, sent to the server (empty = all).
  const [statusFilter, setStatusFilter] = useState('');
  // QA #12: server-side total of failed files (see loadErrorFileTotal).
  const [errorFileTotal, setErrorFileTotal] = useState(0);
  const [sortBy] = useState<FileSortField>('created_at');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');

  // 上傳相關狀態
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UploadFileStatus[]>([]);
  // QA #74: the blocklist the API enforces, from GET /auth/config.
  const uploadPolicy = useUploadPolicy();
  // QA #70: the fastest live upload wins; before anything uploads, the
  // browser's link estimate (Chromium only) stands in.
  const adviceSpeed = (() => {
    const measured = Math.max(0, ...uploadFiles.map((f) => (f.status === 'uploading' && f.speed ? f.speed : 0)));
    if (measured > 0) return { bytesPerSec: measured, source: 'measured' as const };
    const mbps = browserLinkMbps();
    return mbps
      ? { bytesPerSec: (mbps * 1_000_000) / 8, source: 'estimated' as const }
      : { bytesPerSec: 0, source: 'unknown' as const };
  })();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 檔案詳情彈出視窗狀態
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<BackupFile | null>(null);

  // 清除失敗檔案對話框狀態
  const [clearErrorDialogOpen, setClearErrorDialogOpen] = useState(false);
  const [clearingErrors, setClearingErrors] = useState(false);

  // 刪除確認對話框狀態
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // 還原檔案對話框狀態
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [fileToRestore, setFileToRestore] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  // 強制刪除對話框狀態
  const [forceDeleteDialogOpen, setForceDeleteDialogOpen] = useState(false);
  const [fileToForceDelete, setFileToForceDelete] = useState<string | null>(null);
  const [forceDeleting, setForceDeleting] = useState(false);

  // 清空回收站對話框狀態
  const [emptyRecycleBinDialogOpen, setEmptyRecycleBinDialogOpen] = useState(false);
  const [emptyingRecycleBin, setEmptyingRecycleBin] = useState(false);

  // A-05 / ADR-022: files the worker flagged as corrupted. The backend has
  // exposed the list since ADR-022; the console never showed it, so a
  // customer's unrecoverable file was only visible in the database. QA #44:
  // every role sees the section, scoped like the file list (the backend
  // applies the scope; this text only says what the viewer is looking at).
  const currentUser = useAuthStore((s) => s.user);
  const corruptedScopeHint =
    currentUser?.role === 'admin'
      ? '這裡列出所有使用者的檔案。'
      : currentUser?.role === 'sub_admin'
      ? '這裡列出你群組內使用者的檔案。'
      : '這裡只列出你自己的檔案。';
  const [corruptedFiles, setCorruptedFiles] = useState<BackupFile[]>([]);
  const [corruptedLoading, setCorruptedLoading] = useState(false);
  const loadCorrupted = useCallback(async () => {
    setCorruptedLoading(true);
    try {
      const result = await fileService.listCorruptedFiles();
      setCorruptedFiles(result.files);
    } catch (error) {
      console.error('Failed to load corrupted files:', error);
    } finally {
      setCorruptedLoading(false);
    }
  }, []);
  useEffect(() => {
    loadCorrupted();
  }, [loadCorrupted]);

  // 關閉上傳確認對話框狀態
  const [closeUploadDialogOpen, setCloseUploadDialogOpen] = useState(false);

  // A single debounced loader drives every list change. Typing in the search
  // box or picking a status resets to page 1 and re-fetches after 300ms (QA
  // #11 status filter, QA #32 clear-to-refetch); paging re-fetches at once. On
  // F5 the component remounts and this fires with empty state, so a refresh
  // also re-fetches (QA #32).
  useEffect(() => {
    const delay = searchKeyword ? 300 : 0;
    const t = setTimeout(() => loadFiles(), delay);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortBy, sortOrder, userFilter, statusFilter, searchKeyword]);

  // QA #93: failures reported while offline are resent on the next visit to
  // the list and as soon as the browser is back online; the list then shows
  // the failed rows.
  useEffect(() => {
    const flush = async () => {
      if (await fileService.flushUploadFailures()) void loadFiles(true);
    };
    void flush();
    window.addEventListener('online', flush);
    return () => window.removeEventListener('online', flush);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Both upload paths hand the file to the worker and return while the row is
  // still「上傳中」: erasure coding, the shard uploads and the readback gate
  // finish asynchronously and the backend's status sync flips the row to
  // ready / error within ~30 s. Poll while any visible row is in that state
  // so the user sees it land without pressing F5.
  const hasUploadingRows = files.some((f) => f.status === 'uploading');
  useEffect(() => {
    if (!hasUploadingRows) return;
    const id = window.setInterval(() => {
      void loadFiles(true);
    }, 10_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUploadingRows]);

  // silent: refresh the rows without the loading overlay (used by the
  // uploading-rows poll below so the table does not flash every 10 s).
  const loadFiles = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      // Param names mirror backend ListOptions form tags (query / sort / order / status)
      const result = await fileService.getFiles({
        page: page + 1, // MUI 從 0 開始，API 從 1 開始
        limit: rowsPerPage,
        query: searchKeyword || undefined,
        status: statusFilter || undefined,
        sort: sortBy,
        order: sortOrder,
        user_id: userFilter || undefined,
      });

      setFiles(result.files);
      setTotalFiles(result.pagination.total || 0);
    } catch (error) {
      console.error('Failed to load files:', error);
    } finally {
      setLoading(false);
    }
  };

  // The "clear failed records" count must reflect all failed files, not just
  // the current page (QA #12), so it comes from the server's total for
  // status=error rather than counting the loaded rows.
  const loadErrorFileTotal = useCallback(async () => {
    try {
      const result = await fileService.getFiles({ page: 1, limit: 1, status: 'error' });
      setErrorFileTotal(result.pagination.total || 0);
    } catch (error) {
      console.error('Failed to load error file count:', error);
    }
  }, []);

  const loadRecycleBin = useCallback(async () => {
    try {
      const result = await fileService.getRecycleBin({
        page: recyclePage + 1, // MUI 從 0 開始，API 從 1 開始
        limit: recycleRowsPerPage,
      });
      setRecycleBin(result.files);
      setRecycleTotal(result.pagination.total || 0);
      setRecycleRetentionDays(result.retentionDays);
    } catch (error) {
      console.error('Failed to load recycle bin:', error);
    }
  }, [recyclePage, recycleRowsPerPage]);

  useEffect(() => {
    loadErrorFileTotal();
  }, [loadErrorFileTotal]);

  useEffect(() => {
    loadRecycleBin();
  }, [loadRecycleBin]);

  const handleDownload = async (file: BackupFile) => {
    try {
      const fileName = file.file_name;
      const fileId = file.id;
      // 傳入 file_size 讓 ≥ 2 GiB 的檔案走 INFRA-ADR-023 D5 streaming path
      const result = await fileService.downloadFile(fileId, fileName, file.file_size);

      if (result.method === 'unsupported') {
        // ≥ 2 GiB 且瀏覽器不支援 File System Access API (如 Safari / Firefox)
        showError(
          '此檔案超過 2 GB，目前瀏覽器不支援大檔案串流下載。請改用 Chrome / Edge，或透過 File Watcher 還原到本機。',
          'warning',
        );
      }
      // 瀏覽器下載不需要額外提示
    } catch (error: unknown) {
      console.error('Download failed:', error);
      // ADR-022: 422 FILE_CORRUPTED → 友善訊息 + 自動 reload (取得最新 status)
      // api.ts 的 response interceptor 在 reject 前把錯誤 wrap 成 Error 並掛
      // .status / .code 欄位（skipGlobalError 與一般路徑皆然）
      const apiErr = error as { status?: number; code?: string };
      if (apiErr?.status === 422 && apiErr?.code === 'FILE_CORRUPTED') {
        showError(
          '此檔案的雲端資料已損壞，無法下載。系統已自動標記為「資料損壞」。\n' +
            '請從原始來源重新備份此檔案。',
        );
        loadFiles();
        return;
      }
      showError('下載失敗！');
    }
  };

  // 開啟刪除確認對話框
  const openDeleteDialog = (fileId: string) => {
    setFileToDelete(fileId);
    setDeleteDialogOpen(true);
  };

  // 確認刪除
  const handleDelete = async () => {
    if (!fileToDelete) return;
    try {
      setDeleting(true);
      await fileService.deleteFile(fileToDelete);
      setDeleteDialogOpen(false);
      setFileToDelete(null);
      loadFiles();
      loadRecycleBin();
    } catch (error) {
      console.error('Failed to delete file:', error);
      showError('刪除失敗！');
    } finally {
      setDeleting(false);
    }
  };

  // 開啟還原對話框
  const openRestoreDialog = (fileId: string) => {
    setFileToRestore(fileId);
    setRestoreDialogOpen(true);
  };

  // 確認還原
  const handleRestoreFromRecycle = async () => {
    if (!fileToRestore) return;
    try {
      setRestoring(true);
      await fileService.restoreFromRecycle(fileToRestore);
      setRestoreDialogOpen(false);
      setFileToRestore(null);
      loadFiles();
      loadRecycleBin();
    } catch (error) {
      console.error('Failed to restore from recycle:', error);
      showError('還原失敗！');
    } finally {
      setRestoring(false);
    }
  };

  // 開啟強制刪除對話框
  const openForceDeleteDialog = (fileId: string) => {
    setFileToForceDelete(fileId);
    setForceDeleteDialogOpen(true);
  };

  // 確認強制刪除
  const handleForceDelete = async () => {
    if (!fileToForceDelete) return;
    try {
      setForceDeleting(true);
      await fileService.forceDelete(fileToForceDelete);
      setForceDeleteDialogOpen(false);
      setFileToForceDelete(null);
      loadRecycleBin();
    } catch (error) {
      console.error('Failed to force delete:', error);
      showError('刪除失敗！');
    } finally {
      setForceDeleting(false);
    }
  };

  // 確認清空回收站
  const handleEmptyRecycleBin = async () => {
    try {
      setEmptyingRecycleBin(true);
      await fileService.emptyRecycleBin();
      setEmptyRecycleBinDialogOpen(false);
      loadRecycleBin();
    } catch (error) {
      console.error('Failed to empty recycle bin:', error);
      showError('清空失敗！');
    } finally {
      setEmptyingRecycleBin(false);
    }
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  const handleRecyclePaginationModelChange = (model: GridPaginationModel) => {
    setRecyclePage(model.page);
    setRecycleRowsPerPage(model.pageSize);
  };

  // 清除失敗檔案
  const handleClearErrorFiles = async () => {
    try {
      setClearingErrors(true);
      const result = await fileService.clearErrorFiles();
      setClearErrorDialogOpen(false);
      if (result.skipped > 0) {
        showError(`已清除 ${result.count} 筆失敗記錄，另有 ${result.skipped} 筆因儲存服務暫時無法連線而保留，請稍後再試`);
      } else {
        showSuccess(`已成功清除 ${result.count} 筆失敗記錄`);
      }
      loadFiles();
      loadErrorFileTotal();
    } catch (error) {
      console.error('Failed to clear error files:', error);
      showError('清除失敗記錄時發生錯誤');
    } finally {
      setClearingErrors(false);
    }
  };

  // 計算失敗檔案數量
  const errorFileCount = errorFileTotal;

  // 上傳相關函數
  const handleOpenUploadDialog = () => {
    setUploadDialogOpen(true);
    setUploadFiles([]);
  };

  const handleCloseUploadDialog = () => {
    if (isUploading) {
      setCloseUploadDialogOpen(true);
      return;
    }
    closeUploadDialogAndRefresh();
  };

  // QA #9: cancel one in-flight upload (row button) or all of them (closing
  // the dialog while uploading). The loop's catch turns the abort into a
  // 'cancelled' row and tells the server to drop the session.
  const abortControllersRef = useRef<Map<number, AbortController>>(new Map());
  const handleCancelUpload = (index: number) => {
    abortControllersRef.current.get(index)?.abort();
  };
  const abortAllUploads = () => {
    abortControllersRef.current.forEach((c) => c.abort());
    abortControllersRef.current.clear();
  };

  const closeUploadDialogAndRefresh = () => {
    abortAllUploads();
    setUploadDialogOpen(false);
    setCloseUploadDialogOpen(false);
    // 上傳完成後刷新檔案列表
    if (uploadFiles.some((f) => f.status === 'success')) {
      loadFiles();
    }
    setUploadFiles([]);
  };

  const handleSelectFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;
    const list = Array.from(selectedFiles);
    // 清空 input 以便可以再次選擇相同檔案
    event.target.value = '';
    await ingestFiles(list);
  };

  // 拖放上傳（QA #8）：把選檔與拖放共用同一段納入流程。
  const [dragActive, setDragActive] = useState(false);
  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
    const list = Array.from(event.dataTransfer.files || []);
    if (list.length > 0) await ingestFiles(list);
  };

  const ingestFiles = async (selectedFiles: File[]) => {
    if (selectedFiles.length === 0) return;

    const startIndex = uploadFiles.length;
    // 後端 MaxFileSize = 256 GiB：超過的檔案在 hash 之前就標記為 blocked，不進入上傳流程。
    // 0-byte 檔案同樣擋下（N-1，產品決策 2026-09-08）：後端 upload/init 回 FILE_EMPTY，
    // 這裡先擋省掉 hash 與一次必失敗的請求。
    // 可執行檔類型同樣先擋（QA #10 / D2）：後端 upload/init 回 FILE_TYPE_NOT_ALLOWED，
    // 這裡先擋省掉 hash 與一次必失敗的請求（後端才是強制來源）。
    const newFiles: UploadFileStatus[] = Array.from(selectedFiles).map((file) => {
      const tooLarge = file.size > MAX_FILE_SIZE_BYTES;
      const empty = file.size === 0;
      // QA #74: the deployment's list from /auth/config, not the built-in copy.
      const blockedExt = blockedUploadExtension(file.name, uploadPolicy.blockedExtensions);
      let error: string | undefined;
      if (tooLarge) error = '檔案大小超過 256 GiB 上限，無法上傳';
      else if (empty) error = '空檔案（0 byte）不備份';
      else if (blockedExt) error = `不允許上傳 .${blockedExt} 類型的檔案`;
      return {
        file,
        hashProgress: 0,
        progress: 0,
        status: error ? ('blocked' as const) : ('pending' as const),
        error,
      };
    });

    const blocked = newFiles.filter((f) => f.status === 'blocked');
    if (blocked.length > 0) {
      showError(`以下檔案未通過上傳限制，已略過：${blocked.map((f) => f.file.name).join('、')}`);
    }

    setUploadFiles((prev: UploadFileStatus[]) => [...prev, ...newFiles]);

    // 為每個新檔案計算 hash 並進行預檢查 (略過因大小被阻擋的檔案)
    for (let i = 0; i < newFiles.length; i++) {
      if (newFiles[i].status === 'blocked') continue;
      const fileIndex = startIndex + i;
      await hashAndPreCheckFile(fileIndex, newFiles[i].file);
    }
  };

  // 計算 hash 並進行預檢查
  const hashAndPreCheckFile = async (index: number, file: File) => {
    // 更新狀態為計算 hash 中
    setUploadFiles((prev: UploadFileStatus[]) =>
      prev.map((f: UploadFileStatus, idx: number) => (idx === index ? { ...f, status: 'hashing' as const } : f))
    );

    try {
      // 計算 hash
      const hash = await calculateFileHashWithProgress(file, (progress: number) => {
        setUploadFiles((prev: UploadFileStatus[]) =>
          prev.map((f: UploadFileStatus, idx: number) => (idx === index ? { ...f, hashProgress: progress } : f))
        );
      });

      // 更新 hash 並進入預檢查狀態
      setUploadFiles((prev: UploadFileStatus[]) =>
        prev.map((f: UploadFileStatus, idx: number) => (idx === index ? { ...f, hash, status: 'checking' as const } : f))
      );

      // 進行預檢查
      const preCheckResult = await fileService.preCheckFile({
        file_name: file.name,
        file_hash: hash,
        file_size: file.size,
      });

      // 根據預檢查結果更新狀態
      if (!preCheckResult.can_upload) {
        setUploadFiles((prev: UploadFileStatus[]) =>
          prev.map((f: UploadFileStatus, idx: number) =>
            idx === index
              ? { ...f, status: 'blocked' as const, error: preCheckResult.message, preCheckResult }
              : f
          )
        );
      } else {
        setUploadFiles((prev: UploadFileStatus[]) =>
          prev.map((f: UploadFileStatus, idx: number) =>
            idx === index
              ? { ...f, status: 'pending' as const, preCheckResult }
              : f
          )
        );
      }
    } catch (error) {
      setUploadFiles((prev: UploadFileStatus[]) =>
        prev.map((f: UploadFileStatus, idx: number) =>
          idx === index
            ? { ...f, status: 'error' as const, error: '檢查失敗: ' + (error instanceof Error ? error.message : '未知錯誤') }
            : f
        )
      );
    }
  };

  const handleRemoveFile = (index: number) => {
    const status = uploadFiles[index].status;
    if (status === 'uploading' || status === 'hashing' || status === 'checking') return;
    setUploadFiles((prev: UploadFileStatus[]) => prev.filter((_: UploadFileStatus, i: number) => i !== index));
  };

  const handleStartUpload = async () => {
    if (uploadFiles.length === 0 || isUploading) return;

    setIsUploading(true);

    for (let i = 0; i < uploadFiles.length; i++) {
      const fileStatus = uploadFiles[i];
      // 只上傳 pending 狀態（已通過預檢查）的檔案
      if (fileStatus.status !== 'pending') continue;

      // 更新狀態為上傳中
      setUploadFiles((prev: UploadFileStatus[]) =>
        prev.map((f: UploadFileStatus, idx: number) => (idx === i ? { ...f, status: 'uploading' as const } : f))
      );

      // QA #9: one AbortController per file so the row's cancel button (and
      // closing the dialog) can stop the in-flight requests.
      const controller = new AbortController();
      abortControllersRef.current.set(i, controller);

      try {
        const result = await fileService.uploadFile(fileStatus.file, {
          fileHash: fileStatus.hash,
          reuseStorage: fileStatus.preCheckResult?.reuse_storage,
          signal: controller.signal,
          // ADR-054 D2: a network outage pauses the upload for up to the
          // resume window; the row says so until it resumes.
          resumeWindowMs: uploadPolicy.resumeWindowMs,
          onPauseChange: (pause) => {
            setUploadFiles((prev: UploadFileStatus[]) =>
              prev.map((f: UploadFileStatus, idx: number) => (idx === i ? { ...f, paused: pause !== null } : f))
            );
          },
          onProgress: (progress: number, speed?: number) => {
            setUploadFiles((prev: UploadFileStatus[]) =>
              prev.map((f: UploadFileStatus, idx: number) =>
                idx === i ? { ...f, progress, speed: speed && speed > 0 ? speed : f.speed } : f
              )
            );
          },
        });

        // 更新狀態為成功
        setUploadFiles((prev: UploadFileStatus[]) =>
          prev.map((f: UploadFileStatus, idx: number) =>
            idx === i
              ? { ...f, status: 'success' as const, progress: 100, result }
              : f
          )
        );
      } catch (error) {
        // 使用者取消：不再回頭 pre-check（那一支 API 正是 QA #9 看到的多餘呼叫），
        // 通知後端關閉 session 並清掉已上傳的 chunk。
        if (controller.signal.aborted) {
          abortControllersRef.current.delete(i);
          setUploadFiles((prev: UploadFileStatus[]) =>
            prev.map((f: UploadFileStatus, idx: number) =>
              idx === i ? { ...f, status: 'cancelled' as const, error: '已取消上傳' } : f
            )
          );
          // Only the presigned chunked path has a server-side session to close;
          // a cancelled single-shot POST has nothing to abort (it answered 404).
          if (fileStatus.hash && usesChunkedUpload(fileStatus.file)) {
            fileService.abortUpload(fileStatus.hash).catch(() => {
              /* session already gone — nothing to clean */
            });
          }
          continue;
        }

        // QA #102: report the failure first — before the re-check below, whose
        // own 401 used to log the user out before the report was sent. When
        // the login session has ended the report waits in localStorage and is
        // sent right after the next login.
        const sessionExpired = isSessionExpiredError(error);
        if (fileStatus.hash) {
          void fileService.reportUploadFailure(
            {
              file_hash: fileStatus.hash,
              file_name: fileStatus.file.name,
              file_size: fileStatus.file.size,
            },
            { sessionExpired },
          );
        }

        if (sessionExpired) {
          // No re-check (it would only 401 again) and no 「上傳系統失敗」: the
          // file is fine, the login is not.
          setUploadFiles((prev: UploadFileStatus[]) =>
            prev.map((f: UploadFileStatus, idx: number) =>
              idx === i ? { ...f, status: 'error' as const, error: UPLOAD_SESSION_EXPIRED_MESSAGE } : f
            )
          );
          // The files still queued would fail the same way; leave them pending.
          break;
        }

        // 上傳失敗時，重新檢查檔案是否已經存在（可能後端已處理但回應失敗）
        try {
          const reCheckResult = await fileService.preCheckFile({
            file_name: fileStatus.file.name,
            file_hash: fileStatus.hash || '',
            file_size: fileStatus.file.size,
          });

          if (!reCheckResult.can_upload && reCheckResult.message?.includes('已存在')) {
            // 檔案實際上已經上傳成功，只是回應時發生錯誤
            setUploadFiles((prev: UploadFileStatus[]) =>
              prev.map((f: UploadFileStatus, idx: number) =>
                idx === i
                  ? { ...f, status: 'success' as const, progress: 100, error: undefined }
                  : f
              )
            );
            continue;
          }
        } catch {
          // 重新檢查也失敗，忽略
        }

        // 確實是上傳失敗 — QA #64 (PM wording) for the system stage, with the
        // technical reason after it so support can still act on it.
        setUploadFiles((prev: UploadFileStatus[]) =>
          prev.map((f: UploadFileStatus, idx: number) =>
            idx === i
              ? {
                  ...f,
                  status: 'error' as const,
                  error:
                    '上傳系統失敗，請重新上傳' +
                    (error instanceof Error && error.message ? `（${error.message}）` : ''),
                }
              : f
          )
        );
      }
    }

    setIsUploading(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'ready':
        return 'success.main';
      case 'uploading':
        return 'warning.main';
      case 'error':
        return 'error.main';
      case 'corrupted':
        // ADR-022: 雲端 shard 缺失，比 error 更嚴重 (使用者已認為上傳成功)
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'ready':
        return '已完成';
      case 'uploading':
        return '上傳中';
      case 'error':
        return '失敗';
      case 'corrupted':
        // ADR-022
        return '⚠ 資料損壞';
      default:
        return '未知';
    }
  };

  const getUploadStatusIcon = (status: UploadFileStatus['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'blocked':
        return <BlockIcon color="warning" />;
      case 'cancelled':
        return <BlockIcon color="disabled" />;
      case 'uploading':
        return <CloudUploadIcon color="primary" />;
      case 'hashing':
        return <FingerprintIcon color="info" />;
      case 'checking':
        return <CircularProgress size={20} />;
      default:
        return <InsertDriveFileIcon color="action" />;
    }
  };

  const uploadStats = {
    total: uploadFiles.length,
    pending: uploadFiles.filter((f: UploadFileStatus) => f.status === 'pending').length,
    hashing: uploadFiles.filter((f: UploadFileStatus) => f.status === 'hashing').length,
    checking: uploadFiles.filter((f: UploadFileStatus) => f.status === 'checking').length,
    uploading: uploadFiles.filter((f: UploadFileStatus) => f.status === 'uploading').length,
    success: uploadFiles.filter((f: UploadFileStatus) => f.status === 'success').length,
    error: uploadFiles.filter((f: UploadFileStatus) => f.status === 'error').length,
    blocked: uploadFiles.filter((f: UploadFileStatus) => f.status === 'blocked').length,
    cancelled: uploadFiles.filter((f: UploadFileStatus) => f.status === 'cancelled').length,
  };

  const fileColumns: GridColDef<BackupFile>[] = useMemo(
    () => [
      {
        field: 'file_name',
        headerName: '檔案名稱',
        flex: 2,
        minWidth: 150,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600} noWrap>
            {params.row.file_name || params.row.name}
          </Typography>
        ),
      },
      {
        field: 'file_hash',
        headerName: 'Hash',
        flex: 1,
        minWidth: 100,
        renderCell: (params) => <code style={{ fontSize: '0.8em' }}>{params.value?.substring(0, 10)}...</code>,
      },
      {
        field: 'file_size',
        headerName: '大小',
        flex: 0.8,
        minWidth: 80,
        valueFormatter: (value: number) => formatFileSize(value),
      },
      {
        field: 'status',
        headerName: '狀態',
        flex: 0.6,
        minWidth: 96,
        type: 'singleSelect',
        valueOptions: ['ready', 'uploading', 'error', 'corrupted'],
        renderCell: (params) =>
          params.value ? (
            // QA #64: a failed file says why, right next to「失敗」(hover for the whole text).
            <Tooltip title={params.value === 'error' && params.row.error_message ? params.row.error_message : ''} arrow>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: getStatusColor(params.value), fontWeight: 600 }}
                >
                  {getStatusText(params.value)}
                </Typography>
                {params.value === 'error' && params.row.error_message && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {params.row.error_message}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          ),
      },
      {
        field: 'backup_source',
        headerName: '備份來源',
        flex: 0.8,
        minWidth: 90,
        type: 'singleSelect',
        valueOptions: [
          { value: 'manual', label: '手動上傳' },
          { value: 'watcher', label: 'Watcher' },
          { value: 'schedule', label: '排程' },
        ],
        renderCell: (params) => {
          const val = params.value || 'manual';
          return <Chip label={BACKUP_SOURCE_LABELS[val] || val} color={BACKUP_SOURCE_COLORS[val] || 'default'} size="small" />;
        },
      },
      {
        field: 'watcher',
        headerName: 'Watcher',
        flex: 0.8,
        minWidth: 80,
        valueGetter: (_value: any, row: any) => row.watcher?.name || '',
      },
      {
        field: 'uploaded_by',
        headerName: '備份者',
        flex: 0.7,
        minWidth: 70,
        valueGetter: (_value: any, row: any) => row.uploaded_by?.name || '',
      },
      {
        field: 'created_at',
        headerName: '建立時間',
        flex: 1.2,
        minWidth: 140,
        valueFormatter: (value: string) =>
          value ? new Date(value).toLocaleString('zh-TW') : '',
      },
      {
        field: 'actions',
        headerName: '操作',
        // Three small icon buttons (34px each + 8px gaps) need ~120px plus
        // cell padding. With flex 0.8 / minWidth 100 the column shrank on a
        // laptop-width table once the source / watcher / uploader columns
        // were added (QA #42), and the DataGrid cell clipped the leftmost
        // button — 檢視詳情 — out of sight (QA 2026-09-16 「檔案詳情又消失了」).
        // Same class of bug as the schedule page's hidden 執行 button.
        width: 140,
        sortable: false,
        filterable: false,
        resizable: false,
        align: 'right',
        headerAlign: 'right',
        cellClassName: 'files-actions-cell',
        renderCell: (params) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ whiteSpace: 'nowrap' }}>
            <IconButton
              size="small"
              color="info"
              onClick={() => {
                setSelectedFile(params.row);
                setDetailDialogOpen(true);
              }}
              title="檢視詳情"
              aria-label="檢視詳情"
            >
              <InfoIcon />
            </IconButton>
            <IconButton
              size="small"
              color="success"
              onClick={() => handleDownload(params.row)}
              disabled={params.row.status !== 'ready'}
              title={
                params.row.status === 'corrupted'
                  ? '雲端資料損壞，請重新備份此檔案 (ADR-022)'
                  : params.row.status === 'error'
                  ? '檔案上傳失敗，無法下載'
                  : params.row.status === 'uploading'
                  ? '上傳中，尚不可下載'
                  : '下載檔案'
              }
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => openDeleteDialog(params.row.id)}
              title="刪除檔案"
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    []
  );

  const recycleBinColumns: GridColDef<RecycleBinFile>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: '檔案資訊',
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              {params.row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.path}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'deletedTime',
        headerName: '刪除時間',
        width: 170,
      },
      {
        field: 'remainingDays',
        headerName: '剩餘天數',
        width: 100,
        renderCell: (params) =>
          params.value == null ? (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: params.value < 3 ? 'error.main' : 'warning.main',
                fontWeight: 600,
              }}
            >
              剩餘 {params.value} 天
            </Typography>
          ),
      },
      {
        field: 'actions',
        headerName: '操作',
        width: 220,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<RestoreIcon />}
              onClick={() => openRestoreDialog(params.row.id)}
            >
              還原
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => openForceDeleteDialog(params.row.id)}
            >
              強制刪除
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            備份檔案管理
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            查看、下載、刪除備份檔案
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {errorFileCount > 0 && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setClearErrorDialogOpen(true)}
              disabled={loading}
              size="small"
            >
              清除失敗記錄 ({errorFileCount})
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={handleOpenUploadDialog}
            size="large"
          >
            選擇檔案上傳
          </Button>
        </Stack>
      </Box>

      {/* 已備份檔案列表 */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" mb={2}>
          已備份檔案列表
        </Typography>

        {/* 搜尋列 */}
        <Stack direction="row" spacing={2} mb={2} alignItems="center">
          <TextField
            size="small"
            placeholder="搜尋檔案名稱..."
            value={searchKeyword}
            onChange={(e) => {
              setSearchKeyword(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="file-status-filter-label">狀態</InputLabel>
            <Select
              labelId="file-status-filter-label"
              label="狀態"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">全部</MenuItem>
              <MenuItem value="ready">已完成</MenuItem>
              <MenuItem value="uploading">上傳中</MenuItem>
              <MenuItem value="error">失敗</MenuItem>
              <MenuItem value="corrupted">資料損壞</MenuItem>
            </Select>
          </FormControl>
          <RefreshButton onClick={() => loadFiles()} loading={loading} />
        </Stack>

        {/* 檔案列表 */}
        <DataGrid
          rows={files}
          columns={fileColumns}
          loading={loading}
          paginationMode="server"
          rowCount={totalFiles}
          paginationModel={{ page, pageSize: rowsPerPage }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          // Column filtering would only filter the loaded page; the status
          // filter above is the server-side one (QA #11).
          disableColumnFilter
          autoHeight
          getRowId={(row) => row.file_id || row.id}
          localeText={{ noRowsLabel: '暫無檔案' }}
          slotProps={{
            pagination: {
              labelRowsPerPage: '每頁筆數：',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} / 共 ${count}`,
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
            '& .files-actions-cell': { overflow: 'visible' },
          }}
        />
      </Paper>

      {/* 資料損壞的檔案（所有角色；範圍同檔案列表，QA #44） */}
      {(
        <Paper sx={{ p: 3, mt: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6">資料損壞的檔案</Typography>
              <Typography variant="body2" color="text.secondary">
                雲端碎片已缺失、無法重組的檔案。下載會被拒絕；需要從來源重新備份。{corruptedScopeHint}
              </Typography>
            </Box>
            <RefreshButton onClick={loadCorrupted} loading={corruptedLoading} />
          </Stack>
          {corruptedFiles.length === 0 && !corruptedLoading ? (
            <Typography variant="body2" color="text.secondary">
              目前沒有損壞的檔案。
            </Typography>
          ) : (
            <DataGrid
              rows={corruptedFiles}
              loading={corruptedLoading}
              autoHeight
              density="compact"
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              columns={[
                { field: 'file_name', headerName: '檔案名稱', flex: 1.4, minWidth: 200 },
                {
                  field: 'file_size',
                  headerName: '大小',
                  width: 110,
                  valueFormatter: (value: number) => formatFileSize(value),
                },
                {
                  field: 'backup_source',
                  headerName: '來源',
                  width: 100,
                  renderCell: (params) => <Chip size="small" label={params.value || 'manual'} />,
                },
                {
                  field: 'updated_at',
                  headerName: '標記時間',
                  width: 170,
                  valueFormatter: (value: string) => (value ? new Date(value).toLocaleString('zh-TW') : '-'),
                },
                {
                  field: 'status',
                  headerName: '狀態',
                  width: 110,
                  renderCell: () => <Chip size="small" color="error" label="資料損壞" />,
                },
              ]}
            />
          )}
        </Paper>
      )}

      {/* 回收站 */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            回收站{recycleRetentionDays != null && ` (${recycleRetentionDays}天反悔期)`}
          </Typography>
          <Button
            variant="contained"
            color="warning"
            onClick={() => setEmptyRecycleBinDialogOpen(true)}
            disabled={recycleTotal === 0}
          >
            清空回收站
          </Button>
        </Stack>

        <DataGrid
          rows={recycleBin}
          columns={recycleBinColumns}
          paginationMode="server"
          rowCount={recycleTotal}
          paginationModel={{ page: recyclePage, pageSize: recycleRowsPerPage }}
          onPaginationModelChange={handleRecyclePaginationModelChange}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={64}
          getRowId={(row) => row.id}
          pageSizeOptions={[10, 20, 50]}
          localeText={{ noRowsLabel: '回收站是空的' }}
          slotProps={{
            pagination: {
              labelRowsPerPage: '每頁筆數：',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} / 共 ${count}`,
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
          }}
        />
      </Paper>

      {/* 刪除檔案確認對話框 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>確認刪除檔案</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除此檔案嗎？檔案將移至回收站。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            取消
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : undefined}
          >
            {deleting ? '刪除中...' : '確認刪除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 清除失敗檔案確認對話框 */}
      <Dialog
        open={clearErrorDialogOpen}
        onClose={() => !clearingErrors && setClearErrorDialogOpen(false)}
      >
        <DialogTitle>確認清除失敗記錄</DialogTitle>
        <DialogContent>
          <Typography>
            確定要清除你目前看得到的 {errorFileCount} 筆失敗檔案記錄嗎？記錄與已上傳的碎片會一併移除，不進回收桶，此操作無法復原。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearErrorDialogOpen(false)} disabled={clearingErrors}>
            取消
          </Button>
          <Button
            onClick={handleClearErrorFiles}
            color="error"
            variant="contained"
            disabled={clearingErrors}
            startIcon={clearingErrors ? <CircularProgress size={16} /> : undefined}
          >
            {clearingErrors ? '清除中...' : '確認清除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 還原檔案確認對話框 */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => !restoring && setRestoreDialogOpen(false)}
      >
        <DialogTitle>確認還原檔案</DialogTitle>
        <DialogContent>
          <Typography>
            確定要還原此檔案嗎？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
            取消
          </Button>
          <Button
            onClick={handleRestoreFromRecycle}
            color="success"
            variant="contained"
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={16} /> : undefined}
          >
            {restoring ? '還原中...' : '確認還原'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 強制刪除確認對話框 */}
      <Dialog
        open={forceDeleteDialogOpen}
        onClose={() => !forceDeleting && setForceDeleteDialogOpen(false)}
      >
        <DialogTitle>確認永久刪除</DialogTitle>
        <DialogContent>
          <Typography color="error">
            確定要永久刪除此檔案嗎？此操作無法復原！
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceDeleteDialogOpen(false)} disabled={forceDeleting}>
            取消
          </Button>
          <Button
            onClick={handleForceDelete}
            color="error"
            variant="contained"
            disabled={forceDeleting}
            startIcon={forceDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {forceDeleting ? '刪除中...' : '永久刪除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 清空回收站確認對話框 */}
      <Dialog
        open={emptyRecycleBinDialogOpen}
        onClose={() => !emptyingRecycleBin && setEmptyRecycleBinDialogOpen(false)}
      >
        <DialogTitle>確認清空回收站</DialogTitle>
        <DialogContent>
          <Typography color="error">
            確定要清空回收站嗎？所有檔案將被永久刪除，此操作無法復原！
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmptyRecycleBinDialogOpen(false)} disabled={emptyingRecycleBin}>
            取消
          </Button>
          <Button
            onClick={handleEmptyRecycleBin}
            color="error"
            variant="contained"
            disabled={emptyingRecycleBin}
            startIcon={emptyingRecycleBin ? <CircularProgress size={16} /> : undefined}
          >
            {emptyingRecycleBin ? '清空中...' : '清空回收站'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 關閉上傳確認對話框 */}
      <Dialog
        open={closeUploadDialogOpen}
        onClose={() => setCloseUploadDialogOpen(false)}
      >
        <DialogTitle>確認關閉</DialogTitle>
        <DialogContent>
          <Typography color="warning.main">
            上傳仍在進行中，確定要關閉嗎？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseUploadDialogOpen(false)}>
            取消
          </Button>
          <Button
            onClick={closeUploadDialogAndRefresh}
            color="warning"
            variant="contained"
          >
            確認關閉
          </Button>
        </DialogActions>
      </Dialog>

      {/* 上傳對話框 */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">上傳檔案</Typography>
            <IconButton onClick={handleCloseUploadDialog} disabled={isUploading}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {/* 隱藏的檔案輸入 */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            multiple
            style={{ display: 'none' }}
          />

          {/* 選擇檔案區域 */}
          <Box
            sx={{
              border: '2px dashed',
              borderColor: dragActive ? 'success.main' : 'primary.main',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 3,
              bgcolor: dragActive ? 'action.hover' : undefined,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
            onClick={handleSelectFiles}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDrop={handleDrop}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" color="primary">
              點擊選擇檔案
            </Typography>
            <Typography variant="body2" color="text.secondary">
              或將檔案拖放到此處（支援多選）
            </Typography>
          </Box>

          {/* QA #70: browser upload is for small batches; large files belong to
              a File Watcher schedule (resumable, survives a closed tab). The
              threshold follows the measured upload speed (or the browser's
              link estimate before anything is uploading). QA #74: say which
              file types the API refuses. */}
          <Alert severity="info" sx={{ mb: 2 }}>
            {uploadAdvice(adviceSpeed.bytesPerSec, adviceSpeed.source)}
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              不接受的檔案類型：{uploadPolicy.blockedExtensions.length > 0 ? uploadPolicy.blockedExtensions.join('、') : '無'}
              ；其餘類型皆可上傳，單檔上限 256 GiB。
            </Typography>
          </Alert>

          {/* 上傳統計 */}
          {uploadFiles.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              總共 {uploadStats.total} 個檔案：
              {uploadStats.hashing > 0 && ` 計算中 ${uploadStats.hashing}`}
              {uploadStats.checking > 0 && ` 檢查中 ${uploadStats.checking}`}
              {uploadStats.pending > 0 && ` 等待上傳 ${uploadStats.pending}`}
              {uploadStats.uploading > 0 && ` 上傳中 ${uploadStats.uploading}`}
              {uploadStats.success > 0 && ` 成功 ${uploadStats.success}`}
              {uploadStats.blocked > 0 && ` 被阻擋 ${uploadStats.blocked}`}
              {uploadStats.error > 0 && ` 失敗 ${uploadStats.error}`}
            </Alert>
          )}

          {/* 檔案列表 */}
          {uploadFiles.length > 0 && (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {uploadFiles.map((fileStatus, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    fileStatus.status === 'pending' ? (
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveFile(index)}
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    ) : fileStatus.status === 'uploading' ? (
                      <IconButton
                        edge="end"
                        onClick={() => handleCancelUpload(index)}
                        size="small"
                        title="取消上傳"
                      >
                        <CloseIcon />
                      </IconButton>
                    ) : null
                  }
                  sx={{
                    bgcolor: fileStatus.status === 'error' ? 'error.lighter' : 'transparent',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemIcon>{getUploadStatusIcon(fileStatus.status)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={500}>
                          {fileStatus.file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({formatFileSize(fileStatus.file.size)})
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {fileStatus.status === 'hashing' && (
                          <>
                            <Typography variant="caption" color="info.main">
                              正在計算檔案特徵碼... {fileStatus.hashProgress}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={fileStatus.hashProgress}
                              sx={{ height: 4, borderRadius: 1, mt: 0.5 }}
                              color="info"
                            />
                          </>
                        )}
                        {fileStatus.status === 'checking' && (
                          <Typography variant="caption" color="info.main">
                            正在檢查重複檔案...
                          </Typography>
                        )}
                        {fileStatus.status === 'uploading' && (
                          <>
                            {fileStatus.paused ? (
                              <Typography variant="caption" color="warning.main" display="block">
                                網路中斷，恢復後自動續傳（最多保留 {formatResumeWindow(uploadPolicy.resumeWindowMs)}）
                              </Typography>
                            ) : fileStatus.speed ? (
                              <Typography variant="caption" color="text.secondary">
                                上傳中 · {formatSpeed(fileStatus.speed)}
                              </Typography>
                            ) : null}
                            <LinearProgress
                              variant="determinate"
                              value={fileStatus.progress}
                              sx={{ height: 6, borderRadius: 1 }}
                            />
                          </>
                        )}
                        {fileStatus.status === 'success' && (
                          // QA #64 (PM wording): the browser→system stage is done; the
                          // system→cloud stage shows in the list once the worker reports.
                          <Typography variant="caption" color="success.main">
                            {fileStatus.result?.status === 'ready'
                              ? '已上傳到系統，雲端備份完成'
                              : '已上傳到系統，雲端備份中（清單會自動更新）'}
                          </Typography>
                        )}
                        {fileStatus.status === 'blocked' && (
                          <Typography variant="caption" color="warning.main">
                            {fileStatus.error}
                          </Typography>
                        )}
                        {fileStatus.status === 'error' && (
                          <Typography variant="caption" color="error.main">
                            {fileStatus.error || '上傳失敗'}
                          </Typography>
                        )}
                        {fileStatus.status === 'pending' && (
                          <Typography variant="caption" color="text.secondary">
                            等待上傳
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {uploadFiles.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              尚未選擇任何檔案
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseUploadDialog} disabled={isUploading}>
            {uploadStats.success > 0 ? '完成' : '取消'}
          </Button>
          <Button onClick={handleSelectFiles} disabled={isUploading}>
            繼續選擇檔案
          </Button>
          <Button
            variant="contained"
            onClick={handleStartUpload}
            disabled={uploadStats.pending === 0 || isUploading}
            startIcon={<CloudUploadIcon />}
          >
            {isUploading ? '上傳中...' : `開始上傳 (${uploadStats.pending})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 檔案詳情對話框 */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <InsertDriveFileIcon color="primary" />
              <Typography variant="h6">檔案詳情</Typography>
            </Stack>
            <IconButton onClick={() => setDetailDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedFile && (
            <Stack spacing={2.5}>
              {/* 檔案名稱 */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  檔案名稱
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                  {selectedFile.file_name || selectedFile.name}
                </Typography>
              </Box>

              {/* 完整 Hash */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  檔案 Hash (SHA-256)
                </Typography>
                <Typography
                  variant="body2"
                  component="code"
                  sx={{
                    bgcolor: 'grey.100',
                    p: 1,
                    borderRadius: 1,
                    display: 'block',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    fontSize: '0.75rem',
                  }}
                >
                  {selectedFile.file_hash}
                </Typography>
              </Box>

              {/* 檔案大小 */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  檔案大小
                </Typography>
                <Typography variant="body1">
                  {formatFileSize(selectedFile.file_size)}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({selectedFile.file_size.toLocaleString()} bytes)
                  </Typography>
                </Typography>
              </Box>

              {/* 狀態 */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  狀態
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {selectedFile.status === 'ready' && <CheckCircleIcon color="success" fontSize="small" />}
                  {selectedFile.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                  {selectedFile.status === 'corrupted' && <ErrorIcon color="error" fontSize="small" />}
                  {selectedFile.status === 'uploading' && <CloudUploadIcon color="warning" fontSize="small" />}
                  <Typography
                    variant="body1"
                    sx={{ color: getStatusColor(selectedFile.status), fontWeight: 600 }}
                  >
                    {getStatusText(selectedFile.status)}
                  </Typography>
                </Stack>
              </Box>

              {/* 失敗原因（僅在狀態為 error 時顯示） */}
              {selectedFile.status === 'error' && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    失敗原因
                  </Typography>
                  <Typography variant="body2">
                    {/* QA #65: the backend's reason, falling back to the generic text */}
                    {selectedFile.error_message || '檔案上傳或處理過程中發生錯誤，請嘗試重新上傳或聯繫管理員。'}
                  </Typography>
                </Alert>
              )}

              {/* ADR-022: 損壞檔說明 */}
              {selectedFile.status === 'corrupted' && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    雲端資料損壞
                  </Typography>
                  <Typography variant="body2">
                    此檔案的雲端 shard 缺失（可能因雲端服務 silent failure），
                    無法重建原始檔案。系統已自動標記為「資料損壞」並禁止下載。
                    <br />
                    建議：請從原始來源重新備份此檔案。如需協助請聯繫管理員。
                  </Typography>
                </Alert>
              )}

              {/* QA #42: 來源與位置——列表回傳的欄位，對話框原本沒有顯示 */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  原始路徑
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {selectedFile.file_path || selectedFile.path || '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  備份來源
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip
                    size="small"
                    label={BACKUP_SOURCE_LABELS[selectedFile.backup_source || 'manual'] || selectedFile.backup_source}
                    color={BACKUP_SOURCE_COLORS[selectedFile.backup_source || 'manual'] || 'default'}
                  />
                  {selectedFile.watcher?.name && (
                    <Typography variant="body2">Watcher：{selectedFile.watcher.name}</Typography>
                  )}
                  {selectedFile.schedule?.name && (
                    <Typography variant="body2">排程：{selectedFile.schedule.name}</Typography>
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  備份者
                </Typography>
                <Typography variant="body1">{selectedFile.uploaded_by?.name || '-'}</Typography>
              </Box>

              {typeof selectedFile.shard_count === 'number' && selectedFile.shard_count > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    碎片數
                  </Typography>
                  <Typography variant="body1">{selectedFile.shard_count}</Typography>
                </Box>
              )}

              {/* 建立時間 */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  建立時間
                </Typography>
                <Typography variant="body1">
                  {selectedFile.created_at
                    ? new Date(selectedFile.created_at).toLocaleString('zh-TW', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })
                    : selectedFile.lastBackupTime || '-'}
                </Typography>
              </Box>

              {/* 更新時間（如果有） */}
              {selectedFile.updated_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    更新時間
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedFile.updated_at).toLocaleString('zh-TW', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </Typography>
                </Box>
              )}

              {/* MIME 類型（如果有） */}
              {selectedFile.mime_type && (
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    檔案類型
                  </Typography>
                  <Typography variant="body1">{selectedFile.mime_type}</Typography>
                </Box>
              )}

              {/* 檔案 ID */}
              <Box>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  檔案 ID
                </Typography>
                <Typography
                  variant="body2"
                  component="code"
                  sx={{
                    bgcolor: 'grey.100',
                    p: 0.5,
                    px: 1,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  {selectedFile.id || selectedFile.file_id}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailDialogOpen(false)}>關閉</Button>
          {selectedFile && selectedFile.status === 'ready' && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={() => {
                handleDownload(selectedFile);
                setDetailDialogOpen(false);
              }}
            >
              下載檔案
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}